import { useMemo } from 'react';
import { endOfMonth, format } from 'date-fns';
import { useAppSettingsDictionary } from '@/shared/api';
import { DATE_FORMAT_SHORT } from '@/shared/config';
import { useAppSettingsStore, useDateLocale } from '@/shared/lib';
import {
  buildTransformedLimitsData,
  useLimits,
  type TransformedLimitsData,
} from '@/widgets/user-profile/limits';
import type { LimitRow, LimitRowKind } from './types';

type Params = {
  enabled: boolean;
};

const LIMIT_KINDS: LimitRowKind[] = ['withdraw', 'deposit'];

export const useDropdownLimits = ({ enabled }: Params) => {
  const { baseAsset } = useAppSettingsStore();
  const { dateLocale } = useDateLocale();
  const { data: limitsData, isLoading: isLimitsLoading } = useLimits({
    baseAsset,
    enabled,
  });
  const { appSettingsData, isAppSettingsLoading } = useAppSettingsDictionary({
    enabled,
  });

  const transformedLimitsMap = useMemo<TransformedLimitsData | null>(
    () =>
      buildTransformedLimitsData(
        limitsData,
        appSettingsData?.value.limitsTypes,
        dateLocale
      ),
    [limitsData, appSettingsData, dateLocale]
  );

  const rows = useMemo<LimitRow[]>(() => {
    if (!transformedLimitsMap) {
      return [];
    }

    return LIMIT_KINDS.flatMap(kind => {
      const item = transformedLimitsMap.limits.find(
        limit => limit.code === kind
      );

      if (!item || item.limit <= 0) {
        return [];
      }

      return [{ kind, used: item.used, limit: item.limit, left: item.left }];
    });
  }, [transformedLimitsMap]);

  const periodEndLabel = useMemo(() => {
    const period = limitsData?.limits[0]?.period;

    if (!period) {
      return '';
    }

    const today = new Date();
    const end = period === 'month' ? endOfMonth(today) : today;

    return format(end, DATE_FORMAT_SHORT, { locale: dateLocale });
  }, [limitsData, dateLocale]);

  return {
    rows,
    periodEndLabel,
    basePrecission: transformedLimitsMap?.basePrecission,
    baseCode: transformedLimitsMap?.baseAsset,
    transformedLimitsMap,
    isVisible: enabled && rows.length > 0,
    isLoading: enabled && (isLimitsLoading || isAppSettingsLoading),
  };
};
